import React, { useEffect, useMemo, useState } from 'react';
import { API_URL } from '../config/apiConfig';
import { useCountryHover } from './countryHoverContext';

const SENTIENTS_HQ_CELL_ID = 457;

/**
 * Attack-mode hover cost/target labels. Isolated so country hover does not
 * re-render the full War Effort map page.
 */
export default function WarEffortAttackHoverStatus({
  attackFromCountry,
  controlledCountries,
  enemyHqAttackInfo,
  missionMode,
  missionId,
  opponentHqCellId,
  userId,
  playerResources,
  isCeaseFireActive,
  displayCountryForCell,
  t
}) {
  const hoveredCell = useCountryHover();

  const validAttackTargetCellId = useMemo(() => {
    if (!attackFromCountry || hoveredCell == null) return null;
    const hoveredNum = Number(hoveredCell);
    if (hoveredNum === SENTIENTS_HQ_CELL_ID) return null;
    if (controlledCountries.includes(hoveredNum) || hoveredNum === attackFromCountry.cellId) return null;
    if (missionMode === 'ffa' && Array.isArray(enemyHqAttackInfo?.attackableHQs) && enemyHqAttackInfo.attackableHQs.some((h) => h.hqCellId === hoveredNum)) {
      return hoveredNum;
    }
    if (enemyHqAttackInfo?.canAttack && opponentHqCellId != null && hoveredNum === opponentHqCellId) {
      return hoveredNum;
    }
    return hoveredNum;
  }, [attackFromCountry, hoveredCell, controlledCountries, enemyHqAttackInfo?.canAttack, enemyHqAttackInfo?.attackableHQs, missionMode, opponentHqCellId]);

  const [attackModeTargetCost, setAttackModeTargetCost] = useState(null);

  useEffect(() => {
    if (!missionId || !attackFromCountry || !validAttackTargetCellId || !userId) {
      setAttackModeTargetCost(null);
      return undefined;
    }
    const hqMatch = missionMode === 'ffa' && Array.isArray(enemyHqAttackInfo?.attackableHQs)
      ? enemyHqAttackInfo.attackableHQs.find((h) => h.hqCellId === validAttackTargetCellId)
      : (enemyHqAttackInfo?.canAttack && validAttackTargetCellId === enemyHqAttackInfo?.hqCellId
        ? { movementCost: enemyHqAttackInfo.movementCost }
        : null);
    if (hqMatch) {
      setAttackModeTargetCost({ cost: hqMatch.movementCost, distanceKm: null, isHqAttack: true });
      return undefined;
    }
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('WarEffortAttackHoverStatus: auth token is required to fetch movement cost');
    }
    let cancelled = false;
    fetch(`${API_URL}/war-effort/movement-cost?missionId=${missionId}&toCellId=${validAttackTargetCellId}&fromCellId=${attackFromCountry.cellId}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled) return;
        if (!data) {
          setAttackModeTargetCost(null);
          return;
        }
        setAttackModeTargetCost({ cost: data.cost, distanceKm: data.distanceKm, isHqAttack: false });
      })
      .catch((err) => {
        if (!cancelled) {
          console.error(err);
          setAttackModeTargetCost(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [missionId, attackFromCountry?.cellId, validAttackTargetCellId, userId, enemyHqAttackInfo?.canAttack, enemyHqAttackInfo?.hqCellId, enemyHqAttackInfo?.movementCost, enemyHqAttackInfo?.attackableHQs, missionMode]);

  return (
    <>
      {attackModeTargetCost && validAttackTargetCellId && (
        <>
          <span>→ {displayCountryForCell(validAttackTargetCellId)}</span>
          {attackModeTargetCost.distanceKm != null && (
            <span style={{ color: '#4ECDC4' }}>{t('warEffort.distanceKm', { km: attackModeTargetCost.distanceKm })}</span>
          )}
          <span style={{ color: '#FFD700', fontWeight: 'bold' }}>
            {attackModeTargetCost.isHqAttack
              ? t('warEffort.attackBarEnergyHq', { cost: attackModeTargetCost.cost })
              : t('warEffort.attackBarEnergy', { cost: attackModeTargetCost.cost })}
          </span>
          {playerResources < attackModeTargetCost.cost && (
            <span style={{ color: '#ff6b6b' }}>{t('warEffort.insufficientEnergy')}</span>
          )}
        </>
      )}
      {(!validAttackTargetCellId || !attackModeTargetCost) && !isCeaseFireActive && (
        <span style={{ color: '#aaa' }}>{t('warEffort.hoverCountryForCost')}</span>
      )}
    </>
  );
}
